import React from "react";
import { motion } from "framer-motion";
import { Shuffle, Zap } from "lucide-react";

export default function Innovation() {
  return (
    <section
      id="innovation"
      data-testid="innovation-section"
      className="relative py-24 md:py-32 bg-[#084392] text-white overflow-hidden"
    >
      <div className="absolute inset-0 dot-bg opacity-20 pointer-events-none" />
      <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-white/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-white/5 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-6"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-bold font-mono tracking-[0.15em]">
            <Zap size={12} /> WORLD FIRST FOR RETAIL
          </div>
          <h2 className="font-heading mt-6 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
            Auto-transformative
            <br />
            strategies.
            <span className="block text-white/60">
              Institutional-grade.
              <br />
              Retail-accessible.
            </span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">
            &ldquo;But what if the market reverses right after my entry?&rdquo; — every
            retail trader, ever. So we built the answer. Each leg re-shapes
            itself in real time as the trend shifts, without you touching the
            screen.
          </p>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { k: "< 40ms", v: "Execution latency" },
              { k: "100%", v: "Leg-level customizable" },
              { k: "24/7", v: "Strategy re-evaluation" },
            ].map((s, i) => (
              <div
                key={i}
                className="rounded-xl border border-white/15 bg-white/5 backdrop-blur p-4"
              >
                <div className="font-heading text-2xl font-black">{s.k}</div>
                <div className="text-xs text-white/70 mt-0.5">{s.v}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right: Transformation visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6"
        >
          <div className="relative rounded-3xl border border-white/15 bg-white/5 backdrop-blur-xl p-6 md:p-8">
            <div className="flex items-center justify-between">
              <div className="font-mono text-xs text-white/70 tracking-[0.15em]">
                / LIVE ADJUSTMENT
              </div>
              <div className="flex items-center gap-2 text-xs font-bold">
                <span className="h-1.5 w-1.5 rounded-full bg-[#10b981] pulse-dot" />
                <span>Adapting</span>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {/* Before */}
              <div className="rounded-2xl border border-white/15 p-4">
                <div className="text-xs font-mono text-white/60">09:45 — MARKET UP</div>
                <div className="mt-1 text-lg font-bold">Bullish call spread</div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <Leg side="BUY" label="24500 CE" color="#10b981" />
                  <Leg side="SELL" label="24700 CE" color="#ef4444" />
                  <Leg side="—" label="Watch" color="rgba(255,255,255,0.3)" />
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center gap-3 text-white/70">
                <div className="flex-1 h-px bg-white/15" />
                <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold">
                  <Shuffle size={12} /> AUTO-TRANSFORM
                </div>
                <div className="flex-1 h-px bg-white/15" />
              </div>

              {/* After */}
              <div className="rounded-2xl border-2 border-[#10b981]/50 bg-[#10b981]/5 p-4">
                <div className="text-xs font-mono text-[#10b981]">10:22 — TREND FLIPPED</div>
                <div className="mt-1 text-lg font-bold">Bearish put spread</div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <Leg side="BUY" label="24500 PE" color="#10b981" />
                  <Leg side="SELL" label="24300 PE" color="#ef4444" />
                  <Leg side="HEDGE" label="24100 PE" color="#f59e0b" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Leg({ side, label, color }) {
  return (
    <div className="rounded-lg border border-white/15 bg-white/5 p-2.5">
      <div
        className="text-[10px] font-mono font-bold"
        style={{ color }}
      >
        {side}
      </div>
      <div className="mt-0.5 text-xs font-semibold text-white">{label}</div>
    </div>
  );
}
